import React, { useState, useEffect } from 'react';
import { authenticatedFetch } from '../utils/auth';
import { clearMapPreferenceCache } from '../utils/mapUtils';

const ProfilePage = ({ user, onLogout }) => {
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('settings'); // 'settings', 'password', 'apikey', 'map-preference'
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [fullNameLoading, setFullNameLoading] = useState(false);
  const [mapViewPreference, setMapViewPreference] = useState('roadmap');
  const [satelliteLabels, setSatelliteLabels] = useState(true);
  const [mapPreferenceLoading, setMapPreferenceLoading] = useState(false);
  // User preferences state
  const [defaultDedupEnabled, setDefaultDedupEnabled] = useState(false);
  const [defaultDigipinLevel, setDefaultDigipinLevel] = useState(10);
  const [userPreferenceLoading, setUserPreferenceLoading] = useState(false);
  // Location preferences have been removed from the application

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch map preferences on component mount
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const token = localStorage.getItem('access_token');
        
        // Fetch map preference
        const mapResponse = await fetch(`${API_BASE_URL}/map-preference`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (mapResponse.ok) {
          const mapData = await mapResponse.json();
          setMapViewPreference(mapData.map_view_preference);
          setSatelliteLabels(mapData.satellite_labels);
        }
        
        // Fetch user preferences
        const userPrefResponse = await fetch(`${API_BASE_URL}/user-preferences`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (userPrefResponse.ok) {
          const userPrefData = await userPrefResponse.json();
          setDefaultDedupEnabled(userPrefData.default_dedup_enabled);
          setDefaultDigipinLevel(userPrefData.default_digipin_level);
        }
        
        // Location preferences have been removed from the application
      } catch (error) {
        console.error('Error fetching preferences:', error);
      }
    };
    fetchPreferences();
  }, [API_BASE_URL]);

  const generateApiKey = async () => {
    setApiKeyLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/generate-api-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('API key generated successfully!');
        window.location.reload();
      } else {
        setMessage(data.detail || 'Failed to generate API key');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const revokeApiKey = async () => {
    if (!window.confirm('Are you sure you want to revoke your API key? This action cannot be undone.')) return;
    setApiKeyLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/revoke-api-key`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('API key revoked successfully!');
        window.location.reload();
      } else {
        setMessage(data.detail || 'Failed to revoke API key');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires');
    onLogout();
  };

  const updateMapPreference = async (preference, labels = null) => {
    setMapPreferenceLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const requestBody = { map_view_preference: preference };
      if (labels !== null) {
        requestBody.satellite_labels = labels;
      }
      
      const response = await fetch(`${API_BASE_URL}/map-preference`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      if (response.ok) {
        setMapViewPreference(preference);
        if (labels !== null) {
          setSatelliteLabels(labels);
        }
        clearMapPreferenceCache(); // Clear cache so other components will fetch the new preference
        setMessage('Map preference updated successfully!');
      } else {
        setMessage(data.detail || 'Failed to update map preference');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setMapPreferenceLoading(false);
    }
  };

  // Location preferences functionality has been removed

  const updateUserPreferences = async () => {
    setUserPreferenceLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/user-preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          default_dedup_enabled: defaultDedupEnabled,
          default_digipin_level: defaultDigipinLevel
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('User preferences updated successfully!');
      } else {
        setMessage(data.detail || 'Failed to update user preferences');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setUserPreferenceLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setMessage('');
    if (newPassword !== confirmNewPassword) {
      setMessage('New passwords do not match');
      setPasswordLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Password changed successfully!');
        setOldPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        setMessage(data.detail || 'Failed to change password');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', maxWidth: 800, margin: '0 auto', padding: 24, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
      {/* Left Navigation */}
      <div style={{ minWidth: 180, borderRight: '1px solid #eee', paddingRight: 24, marginRight: 32 }}>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <button onClick={() => setActiveSection('settings')} style={{ background: 'none', border: 'none', color: activeSection === 'settings' ? '#007bff' : '#333', fontWeight: activeSection === 'settings' ? 700 : 500, fontSize: 16, textAlign: 'left', cursor: 'pointer', padding: 0 }}>Profile</button>
          <button onClick={() => setActiveSection('password')} style={{ background: 'none', border: 'none', color: activeSection === 'password' ? '#007bff' : '#333', fontWeight: activeSection === 'password' ? 700 : 500, fontSize: 16, textAlign: 'left', cursor: 'pointer', padding: 0 }}>Change Password</button>
          <button onClick={() => setActiveSection('apikey')} style={{ background: 'none', border: 'none', color: activeSection === 'apikey' ? '#007bff' : '#333', fontWeight: activeSection === 'apikey' ? 700 : 500, fontSize: 16, textAlign: 'left', cursor: 'pointer', padding: 0 }}>API Key</button>
          <button onClick={() => setActiveSection('map-preference')} style={{ background: 'none', border: 'none', color: activeSection === 'map-preference' ? '#007bff' : '#333', fontWeight: activeSection === 'map-preference' ? 700 : 500, fontSize: 16, textAlign: 'left', cursor: 'pointer', padding: 0 }}>Map Preferences</button>
          <button onClick={() => setActiveSection('user-preferences')} style={{ background: 'none', border: 'none', color: activeSection === 'user-preferences' ? '#007bff' : '#333', fontWeight: activeSection === 'user-preferences' ? 700 : 500, fontSize: 16, textAlign: 'left', cursor: 'pointer', padding: 0 }}>User Preferences</button>
          {/* My Addresses button is now removed */}
        </nav>
        {/* Logout button removed from here */}
      </div>
      {/* Main Content */}
      <div style={{ flex: 1 }}>
        {activeSection === 'settings' && (
          <div>
            <h2 style={{ marginBottom: 20 }}>Profile</h2>
            <div style={{ marginBottom: 20 }}>
              <p><strong>Email:</strong> {user?.email}</p>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Full Name:</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                style={{ width: '100%', maxWidth: 300, padding: 10, border: '1px solid #ccc', borderRadius: 4, fontSize: 15, boxSizing: 'border-box', marginBottom: 8 }}
                placeholder="Enter your full name"
              />
              <button
                onClick={async () => {
                  setFullNameLoading(true);
                  setMessage('');
                  try {
                    const token = localStorage.getItem('access_token');
                    const response = await fetch(`${API_BASE_URL}/update-profile`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ full_name: fullName }),
                    });
                    const data = await response.json();
                    if (response.ok) {
                      setMessage('Full name updated successfully!');
                    } else {
                      setMessage(data.detail || 'Failed to update full name');
                    }
                  } catch (error) {
                    setMessage('Network error. Please try again.');
                  } finally {
                    setFullNameLoading(false);
                  }
                }}
                disabled={fullNameLoading || !fullName.trim()}
                style={{ padding: '8px 16px', marginLeft: 12, background: '#007bff', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: 15, cursor: fullNameLoading ? 'not-allowed' : 'pointer' }}
              >
                {fullNameLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
        {activeSection === 'password' && (
          <div>
            <h2 style={{ marginBottom: 20 }}>Change Password</h2>
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: 10 }}>
                <label>Old Password:</label>
                <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label>New Password:</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label>Confirm New Password:</label>
                <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <button type="submit" disabled={passwordLoading} style={{ width: '100%', padding: 10, background: '#007bff', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: 16, marginBottom: 10 }}>{passwordLoading ? 'Changing...' : 'Change Password'}</button>
            </form>
          </div>
        )}
        {activeSection === 'apikey' && (
          <div>
            <h2 style={{ marginBottom: 20 }}>API Key</h2>
            {user?.api_key ? (
              <div>
                <p><strong>Your API Key:</strong></p>
                <div style={{ padding: 10, background: '#f9f9f9', border: '1px solid #dee2e6', borderRadius: 4, fontfamily: 'monospace', fontSize: 12, wordBreak: 'break-all', marginBottom: 10 }}>{user.api_key}</div>
                <button onClick={revokeApiKey} disabled={apiKeyLoading} style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: apiKeyLoading ? 'not-allowed' : 'pointer', fontSize: 14 }}>{apiKeyLoading ? 'Revoking...' : 'Revoke API Key'}</button>
              </div>
            ) : user?.is_admin ? (
              <div>
                <p style={{ color: '#666', marginBottom: 10 }}>Admin accounts cannot generate API keys for security reasons.</p>
                <p style={{ color: '#666', fontSize: 14 }}>If you need API access, please contact the system administrator.</p>
              </div>
            ) : (
              <div>
                <p style={{ color: '#666', marginBottom: 10 }}>No API key generated yet. Generate one to access the API programmatically.</p>
                <button onClick={generateApiKey} disabled={apiKeyLoading} style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: 4, cursor: apiKeyLoading ? 'not-allowed' : 'pointer', fontSize: 14 }}>{apiKeyLoading ? 'Generating...' : 'Generate API Key'}</button>
              </div>
            )}
          </div>
        )}
        {activeSection === 'map-preference' && (
          <div>
            <h2 style={{ marginBottom: 20 }}>Map Preferences</h2>
            <div style={{ marginBottom: 20 }}>
              <p style={{ marginBottom: 15, color: '#666' }}>
                Choose your preferred map view type. This setting will be applied to all maps across the application.
              </p>
              <div style={{ display: 'flex', gap: 15, marginBottom: 15 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="mapView"
                    value="roadmap"
                    checked={mapViewPreference === 'roadmap'}
                    onChange={() => updateMapPreference('roadmap')}
                    disabled={mapPreferenceLoading}
                    style={{ cursor: mapPreferenceLoading ? 'not-allowed' : 'pointer' }}
                  />
                  <span style={{ fontWeight: 500 }}>Standard View</span>
                </label>
                                 <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                   <input
                     type="radio"
                     name="mapView"
                     value="satellite"
                     checked={mapViewPreference === 'satellite'}
                     onChange={() => updateMapPreference('satellite')}
                     disabled={mapPreferenceLoading}
                     style={{ cursor: mapPreferenceLoading ? 'not-allowed' : 'pointer' }}
                   />
                   <span style={{ fontWeight: 500 }}>Satellite View</span>
                 </label>
               </div>
               {mapViewPreference === 'satellite' && (
                 <div style={{ marginTop: 15, padding: 15, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
                   <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                     <input
                       type="checkbox"
                       checked={satelliteLabels}
                       onChange={(e) => updateMapPreference('satellite', e.target.checked)}
                       disabled={mapPreferenceLoading}
                       style={{ cursor: mapPreferenceLoading ? 'not-allowed' : 'pointer' }}
                     />
                     <span style={{ fontWeight: 500 }}>Show labels in satellite view</span>
                   </label>
                   <p style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                     When enabled, street names and landmarks will be visible on satellite imagery.
                   </p>
                 </div>
               )}
              <div style={{ fontSize: 14, color: '#666' }}>
                <p><strong>Standard View:</strong> Traditional map with roads, landmarks, and street names</p>
                <p><strong>Satellite View:</strong> Aerial imagery with optional labels</p>
              </div>
            </div>
          </div>
        )}
        {activeSection === 'user-preferences' && (
          <div>
            <h2 style={{ marginBottom: 20 }}>User Preferences</h2>
            <div style={{ marginBottom: 20 }}>
              <p style={{ marginBottom: 15, color: '#666' }}>
                Configure your default preferences for the Pin2Point application. These settings will be applied when you open the application.
              </p>
              
              {/* Dedup Preference */}
              <div style={{ marginBottom: 20, padding: 15, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 10 }}>
                  <input
                    type="checkbox"
                    checked={defaultDedupEnabled}
                    onChange={(e) => setDefaultDedupEnabled(e.target.checked)}
                    disabled={userPreferenceLoading}
                    style={{ cursor: userPreferenceLoading ? 'not-allowed' : 'pointer' }}
                  />
                  <span style={{ fontWeight: 500 }}>Enable De-Dup by default</span>
                </label>
                <p style={{ marginLeft: 24, fontSize: 13, color: '#666' }}>
                  When enabled, the De-Duplication feature will be automatically turned on when you open Pin2Point.
                </p>
              </div>

              {/* Digipin Level Preference */}
              <div style={{ marginBottom: 20, padding: 15, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
                <label style={{ display: 'block', marginBottom: 10, fontWeight: 500 }}>
                  Default Digipin Level: <span style={{ color: '#007bff', fontWeight: 600 }}>{defaultDigipinLevel}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={defaultDigipinLevel}
                  onChange={(e) => setDefaultDigipinLevel(parseInt(e.target.value))}
                  disabled={userPreferenceLoading}
                  style={{
                    width: '100%',
                    height: '6px',
                    borderRadius: '3px',
                    background: '#ddd',
                    outline: 'none',
                    cursor: userPreferenceLoading ? 'not-allowed' : 'pointer'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 12, color: '#666' }}>
                  <span>Level 1 (Broad)</span>
                  <span>Level 10 (Precise)</span>
                </div>
                <p style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                  This level will be automatically selected when you open Pin2Point. Higher levels provide more precise geographic grouping.
                </p>
              </div>

              {/* Save Button */}
              <button
                onClick={updateUserPreferences}
                disabled={userPreferenceLoading}
                style={{
                  padding: '10px 20px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: userPreferenceLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {userPreferenceLoading ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        )}
        {/* The entire addresses section is now removed */}
        {message && <div style={{ marginTop: 12, padding: 10, backgroundColor: message.includes('error') ? '#f8d7da' : '#d4edda', color: message.includes('error') ? '#721c24' : '#155724', borderRadius: 4, fontSize: 15 }}>{message}</div>}
      </div>
    </div>
  );
};

export default ProfilePage; 